import { Badge } from 'antd';
import { BarcodeOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import useScanBufferStore from '../../store/scanBufferStore';
import useBarcodeScanStore from '../../store/barcodeScanStore';
import useScanTargetStore from '../../store/scanTargetStore';
import './ScanBufferHandle.css';

interface Props {
  // 'edge'    — дефолт, fixed-позиционированная вкладка на правом краю экрана
  //             (десктоп и старое мобильное поведение).
  // 'compact' — обычный inline-элемент ~40×40 для вставки в bottom bar
  //             на мобиле. Без fixed, без горизонтальной «ручки».
  variant?: 'edge' | 'compact';
}

const ScanBufferHandle: React.FC<Props> = ({ variant = 'edge' }) => {
  const { count: markingCount, drawerOpen, openDrawer, items, refreshItems } = useScanBufferStore();
  const barcodeCount = useBarcodeScanStore(s => s.items.length);
  const refreshBarcodes = useBarcodeScanStore(s => s.refreshItems);
  const count = markingCount + barcodeCount;
  const targetMarkingCodes = useScanTargetStore(s => s.markingCodes);
  const empty = count === 0;

  const hasDuplicates = items.some(s => s.marking_code && targetMarkingCodes.has(s.marking_code));

  // При mount обновляем items, чтобы hasDuplicates был актуален даже при закрытом drawer
  useEffect(() => {
    if (!drawerOpen) {
      refreshItems();
      refreshBarcodes().catch(() => undefined);
    }
  }, [drawerOpen, refreshItems, refreshBarcodes]);

  // В edge-режиме прячем кнопку, когда drawer открыт — иначе она перекрывает
  // правый край drawer-а. В compact-режиме bottom bar всегда виден, кнопка
  // остаётся на месте.
  if (variant === 'edge' && drawerOpen) return null;

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={openDrawer}
        aria-label={empty ? 'Буфер пуст' : `Буфер: ${count}`}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 0,
          width: 48,
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: empty ? '#bfbfbf' : '#1677ff',
          ...(hasDuplicates ? {
            animation: 'glow-pulse-orange 1.5s ease-in-out infinite',
            outline: '2px solid #faad14',
            outlineOffset: '-2px',
            borderRadius: 4,
          } : {}),
        }}
      >
        <Badge count={count} size="small" offset={[-2, 2]}>
          <BarcodeOutlined style={{ fontSize: 24 }} />
        </Badge>
      </button>
    );
  }

  return (
    <button
      className={`scan-buffer-handle ${empty ? 'scan-buffer-handle--empty' : ''} ${hasDuplicates ? 'scan-buffer-handle--duplicate' : ''}`}
      onClick={openDrawer}
      title={empty ? 'Буфер сканирования пуст' : `Буфер: ${count} код(ов)`}
    >
      <Badge count={count} size="small" offset={[-4, 4]}>
        <div className="scan-buffer-handle__inner">
          <BarcodeOutlined className="scan-buffer-handle__icon" />
          <span className="scan-buffer-handle__label">Буфер</span>
        </div>
      </Badge>
    </button>
  );
};

export default ScanBufferHandle;
